import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { api_without_cancellation as api } from "../../api/api";
import { selectAuthPermissionSubjectUserId } from "../../Redux/authPermissions/authPermissionsSlice";

import { captureFlaggedCaseCountUpdate } from "./flaggedCaseCount";

export default function useCaseFlag({ clientId, caseId, pageId, link, setNewFlags }) {
  const userId = useSelector(selectAuthPermissionSubjectUserId);
  const scope = `${userId}:${clientId}:${caseId}:${pageId}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const mounted = useRef(false);
  const request = useRef(0);
  const flagRef = useRef(null);
  const [state, setState] = useState({ scope: null, flagPage: null, positions: [], loading: true });
  const isCurrent = useCallback(() => mounted.current && scopeRef.current === scope, [scope]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadFlagState = useCallback(async () => {
    if (!clientId || !caseId || !pageId) throw new Error("Open a case page before flagging.");
    const currentRequest = ++request.current;
    if (isCurrent()) setState((previous) => ({ ...previous, loading: true }));
    try {
      const { data } = await api.get(`/api/flaggedcases/${clientId}/${caseId}/`, {
        params: { page_id: pageId },
      });
      if (isCurrent() && currentRequest === request.current) {
        flagRef.current = { scope, flagPage: data.flagPage };
        setState({
          scope,
          flagPage: data.flagPage,
          positions: Array.isArray(data.positions) ? data.positions : [],
          loading: false,
        });
      }
      return data;
    } catch (error) {
      if (isCurrent() && currentRequest === request.current) {
        setState((previous) => ({ ...previous, scope, loading: false }));
      }
      throw error;
    }
  }, [clientId, caseId, pageId, scope, isCurrent]);

  useEffect(() => {
    flagRef.current = null;
    setState({
      scope,
      flagPage: null,
      positions: [],
      loading: Boolean(clientId && caseId && pageId),
    });
    if (clientId && caseId && pageId) loadFlagState().catch(() => {});
    return () => {
      request.current += 1;
    };
  }, [scope, clientId, caseId, pageId, loadFlagState]);

  const finishMutation = useCallback(
    async (data, updateCount, unflagging = false) => {
      if (!isCurrent()) return;
      const countApplied = updateCount(data.activeFlaggedCaseCount);
      // A failed follow-up read must not invite the user to repeat a write
      // that already succeeded. Keep the confirmed mutation state instead.
      if (unflagging || data.flagPage) {
        const flagPage = unflagging ? null : data.flagPage;
        flagRef.current = { scope, flagPage };
        setState((previous) => ({ ...previous, scope, flagPage, loading: false }));
      }
      setNewFlags?.((previous) => previous + 1);
      await loadFlagState()
        .then((fresh) => {
          // Duplicate flags may omit the count from the POST payload.
          if (!countApplied && isCurrent()) updateCount(fresh.activeFlaggedCaseCount);
        })
        .catch(() => {});
    },
    [isCurrent, scope, setNewFlags, loadFlagState]
  );

  const flagCase = useCallback(
    async (reason, assignedStaffIds) => {
      const updateCount = captureFlaggedCaseCountUpdate(userId);
      const { data } = await api.post(`/api/flaggedcases/${clientId}/${caseId}/`, {
        page_id: pageId,
        flagged_reason: reason.trim(),
        assigned_staff_ids: [...new Set(assignedStaffIds)],
        link,
      });
      // "Already exists!" has no flagPage; the follow-up GET resolves it.
      await finishMutation(data, updateCount);
    },
    [userId, clientId, caseId, pageId, link, finishMutation]
  );

  const unflagCase = useCallback(async () => {
    const updateCount = captureFlaggedCaseCountUpdate(userId);
    let refreshedCount;
    let flagPage = flagRef.current?.scope === scope ? flagRef.current.flagPage : null;
    if (!flagPage?.id) {
      const data = await loadFlagState();
      flagPage = data.flagPage;
      refreshedCount = data.activeFlaggedCaseCount;
    }
    if (!isCurrent()) return;
    if (!flagPage?.id) {
      // Someone else removed it before the confirmation was submitted.
      updateCount(refreshedCount);
      setNewFlags?.((previous) => previous + 1);
      return;
    }
    const { data } = await api.delete(`/api/flaggedcases/${clientId}/${caseId}/`, {
      data: { flagPageId: flagPage.id },
    });
    if (!["Deleted", "Not Deleted"].includes(data.data)) {
      throw new Error("The server did not confirm unflagging.");
    }
    await finishMutation(data, updateCount, true);
  }, [scope, loadFlagState, isCurrent, userId, setNewFlags, clientId, caseId, finishMutation]);

  return {
    flagPage: state.scope === scope ? state.flagPage : null,
    positions: state.scope === scope ? state.positions : [],
    isLoading: state.scope !== scope || state.loading,
    loadFlagState,
    flagCase,
    unflagCase,
  };
}
